import { useQuery } from "@tanstack/react-query";
import supabase from "@/supabase";

// This interface is now only used in the cloud function
// Keeping it here for reference of the data structure
// interface Session {
//   id: string;
//   depot_id: string;
//   jobs: string[];
//   date: string;
//   type: string;
//   capacity: number;
//   status: string;
//   start_time: string;
//   end_time: string;
//   recurrence: string;
//   recurrence_end_date: string | null;
//   depot?: {
//     id: string;
//     name: string;
//     address: string;
//     city: string;
//   };
// }

interface AvailableDate {
  date: Date;
  sessionId: string;
  startTime: string;
  endTime: string;
  depot?: {
    name: string;
    address: string;
    city: string;
  };
}

/**
 * Function to call the Supabase Edge Function for session dates
 */
async function fetchSessionDates(
  jobId: string,
  sessionType: "orientation" | "vehicle_collection"
): Promise<AvailableDate[]> {
  try {
    // Call the Supabase Edge Function
    const { data, error } = await supabase.functions.invoke(
      "get-session-dates",
      {
        body: { jobId, sessionType },
      }
    );

    if (error) {
      console.error("Error calling get-session-dates function:", error);
      try {
        // Try to parse the error response body to get the actual error message
        const parsedError = await error.context.json();
        throw new Error(parsedError.message || "Failed to fetch available sessions");
      } catch (parseError) {
        // If parsing fails, fall back to error.message or default message
        throw new Error(error.message || "Failed to fetch available sessions");
      }
    }

    // The Edge Function returns data in the format { data: AvailableDate[] }
    return (
      data?.data.map((d) => ({
        ...d,
        date: new Date(d.date),
      })) || []
    );
  } catch (error) {
    console.error("Error in fetchSessionDates:", error);
    throw error;
  }
}

/**
 * Hook to fetch available session dates for a specific job and session type
 * Uses a Supabase Edge Function to handle the complex logic server-side
 */
export const useSessionDates = (
  jobId: string | undefined,
  sessionType: "orientation" | "vehicle_collection" = "orientation"
) => {
  return useQuery({
    queryKey: ["sessionDates", jobId, sessionType],
    queryFn: async (): Promise<AvailableDate[]> => {
      if (!jobId) {
        return [];
      }

      return fetchSessionDates(jobId, sessionType);
    },
    enabled: !!jobId,
  });
};
